/**
 * Reports > Item File (/reports/item-file).
 *
 * One row per item with its key columns (status, department, vendor, price, last cost,
 * UPC), filters for status, department, vendor and a search, a sortable table, and an
 * Excel download of the 59-column file Tim feeds to a downstream system. Replaces the
 * QuickSight dataset "MapTbl_Products". Store stock moved to Commerce > Inventory.
 *
 * This page is deliberately thin. The screen itself is the shared `ItemFile` component in
 * `@ampersandbrands/ui` (presentational, so The Sweet Spot can reuse it); this file
 * fetches through `useItemFile`, keeps the filters and the sort in the URL so a view is a
 * shareable link, and adds the page chrome: header with the pin toggle and the download.
 *
 * RBAC: `reports.itemfile`, or a store manager (holding `stores.sweetspot`). The item file
 * is not store data, so a store manager sees every item; the api enforces the real rule.
 *
 * Resilience: wrapped in an ErrorBoundary and degrades to a quiet "not available yet"
 * card on 404/503 while the api ships.
 */

import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ItemFile as ItemFileScreen, type ItemFileFilters, type ItemFileSort } from '@ampersandbrands/ui';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ToolPinToggle } from '@/components/ToolPinToggle';
import { useSession } from '@/lib/useSession';
import { useReportPageState } from '@/lib/reportPageState';
import { isNotAvailable } from '@/lib/useStores';
import {
  downloadItemFileXlsx,
  parseItemFilters,
  parseItemSort,
  useItemFile,
  writeItemFilters,
  writeItemSort,
} from '@/lib/useItemFile';
import { hasAccessSlug } from '@/lib/toolVisibility';
import { AccessDeniedCard, ErrorCard, NotAvailableCard, StoresHeader } from '../stores/storesShared';

/** The accessSlug that grants this report. */
const SLUG = 'reports.itemfile';

/** `reports.itemfile`, or a store manager through `stores.sweetspot`. The api enforces the real rule. */
function canViewItemFile(perms: string[] | undefined, superAdmin = false): boolean {
  if (hasAccessSlug(perms, SLUG, superAdmin)) return true;
  return !!perms?.includes('stores.sweetspot');
}

function ItemFileInner() {
  const { user } = useSession();
  const canView = canViewItemFile(user?.permissions, user?.superAdmin);

  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => parseItemFilters(searchParams), [searchParams]);
  const sort = useMemo(() => parseItemSort(searchParams), [searchParams]);
  const [exporting, setExporting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const update = useCallback(
    (write: (sp: URLSearchParams) => void) =>
      setSearchParams(
        (prev) => {
          const sp = new URLSearchParams(prev);
          write(sp);
          return sp;
        },
        { replace: true },
      ),
    [setSearchParams],
  );
  const setFilters = useCallback((next: ItemFileFilters) => update((sp) => writeItemFilters(sp, next)), [update]);
  const setSort = useCallback((next: ItemFileSort) => update((sp) => writeItemSort(sp, next)), [update]);

  const itemsQ = useItemFile(filters, sort, canView);

  const pageState = useMemo(
    () => [
      { label: 'Status', value: filters.status.length > 0 ? filters.status.join(', ') : 'All statuses' },
      { label: 'Departments', value: filters.department.length > 0 ? filters.department.join(', ') : 'All departments' },
      { label: 'Vendors', value: filters.vendor.length > 0 ? filters.vendor.join(', ') : 'All vendors' },
      ...(filters.q ? [{ label: 'Search', value: filters.q }] : []),
      ...(sort.col ? [{ label: 'Sort', value: `${sort.col} ${sort.dir}` }] : []),
    ],
    [filters, sort],
  );
  useReportPageState(pageState);

  async function handleExport() {
    setExporting(true);
    setToast(null);
    try {
      await downloadItemFileXlsx(filters, sort);
    } catch (e) {
      setToast(e instanceof Error ? e.message : 'Download failed');
    } finally {
      setExporting(false);
    }
  }

  if (user && !canView) return <AccessDeniedCard what="Item File" />;

  const header = (
    <StoresHeader
      eyebrow="Reports"
      title="Item File"
      subtitle="Every item with its status, department, vendor, price, last cost and UPC. Download the full 59-column item file as Excel. Refreshed from CounterPoint every hour."
      actions={<ToolPinToggle toolSlug="item-file" toolName="Item File" />}
    />
  );

  if (isNotAvailable(itemsQ.error)) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        {header}
        <NotAvailableCard what="Item File" />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {header}
      {toast && (
        <p role="alert" className="mb-3 text-sm text-red-700 dark:text-red-300">
          {toast}
        </p>
      )}
      <ItemFileScreen
        error={itemsQ.error ? <ErrorCard message={itemsQ.error.message} /> : undefined}
        rows={itemsQ.rows}
        totalRows={itemsQ.totalRows}
        statusOptions={itemsQ.statuses}
        departmentOptions={itemsQ.departments}
        vendorOptions={itemsQ.vendors}
        asOf={itemsQ.asOf}
        loading={itemsQ.isLoading}
        filters={filters}
        onFiltersChange={setFilters}
        sort={sort}
        onSortChange={setSort}
        onExport={() => void handleExport()}
        exporting={exporting}
      />
    </div>
  );
}

export function ItemFile() {
  return (
    <ErrorBoundary>
      <ItemFileInner />
    </ErrorBoundary>
  );
}
